// src/context/Notifications/NotificationsProvider.tsx
import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import env from 'react-dotenv';
import { useCookies } from 'react-cookie';
import { useSSE } from '../../components/SSE/SSEHook';
import {
  getAllShipmentsRTL,
  GetAllShipmentsRequest,
} from '../../services/Api/apiShipments';
import { ShipmentResponse } from '../../interfaces/Shipment/Response/shipmentResponse';
import { listDevices } from '../../services/Api/apiDevices';
import { AppNotification, NotificationsContextValue } from './types';
import {
  MAX_ENTRIES,
  migrate,
  ingest,
  markRead as markReadFn,
  markAllRead as markAllReadFn,
  unreadCount as unreadCountFn,
  isSealType,
  genNotifId,
  notificationStorageKey,
} from './notificationsCache';
import {
  DeviceAlertOverrides,
  setOverride,
  isAlerting,
} from './alertOverrides';
import { resolveSessionCompanyId } from '../../utils/sessionCompanyScope';

const API_BASE_URL = env.REACT_APP_API_BASE_URL;
const DEVICE_LIST_PAGE_SIZE = 200;
const MAX_DEVICE_LIST_PAGES = 1000;
const MAX_SOURCE_DEVICE_IDS = 250;

interface NotificationSubscriptionProps {
  deviceIds: string[];
  authToken?: string;
  onMessage: (data: {
    timestamp: number;
    value: string;
    type: string;
    device_id: string;
  }) => void;
  onError: (error: Event) => void;
}

/** One hook-safe SSE subscription, capped to the orchestrator's URL limit. */
const NotificationSubscription: React.FC<NotificationSubscriptionProps> = ({
  deviceIds,
  authToken,
  onMessage,
  onError,
}) => {
  const url = useMemo(
    () =>
      deviceIds.length > 0 && API_BASE_URL && authToken
        ? `${API_BASE_URL}/alerts/sse?${deviceIds
            .map((id) => `device_ids=${encodeURIComponent(id)}`)
            .join('&')}&channel=notification`
        : null,
    [authToken, deviceIds]
  );

  useSSE({ url, authToken, onMessage, onError });
  return null;
};

export const NotificationsContext =
  createContext<NotificationsContextValue | null>(null);

interface NotificationScope {
  companyId?: string;
  generation: number;
}

interface ScopedNotificationsProviderProps {
  companyId?: string;
  authToken?: string;
  generation: number;
  activeScopeRef: React.MutableRefObject<NotificationScope>;
  children: React.ReactNode;
}

const ScopedNotificationsProvider: React.FC<
  ScopedNotificationsProviderProps
> = ({ companyId, authToken, generation, activeScopeRef, children }) => {
  const isCurrentScope = useCallback(
    () =>
      activeScopeRef.current.companyId === companyId &&
      activeScopeRef.current.generation === generation,
    [activeScopeRef, companyId, generation]
  );

  const [notifications, setNotifications] = useState<AppNotification[]>(() => {
    if (!companyId) return [];
    try {
      const saved = localStorage.getItem(notificationStorageKey(companyId));
      return migrate(saved ? JSON.parse(saved) : [], Date.now());
    } catch {
      return [];
    }
  });
  const [overrides, setOverrides] = useState<DeviceAlertOverrides>({});
  const [rtlSerials, setRtlSerials] = useState<string[]>([]);
  const [imsecureSerials, setImsecureSerials] = useState<string[]>([]);

  // Persist cache changes (already trimmed/filtered by ingest/migrate).
  useEffect(() => {
    if (!companyId || !isCurrentScope()) return;
    try {
      localStorage.setItem(
        notificationStorageKey(companyId),
        JSON.stringify(notifications.slice(0, MAX_ENTRIES))
      );
    } catch {
      // ignore quota / serialization errors
    }
  }, [companyId, isCurrentScope, notifications]);

  // RTL serials from the company's current shipments.
  useEffect(() => {
    if (!companyId || !isCurrentScope()) return;
    let cancelled = false;
    const ownsScope = () => !cancelled && isCurrentScope();
    const run = async () => {
      const payload: GetAllShipmentsRequest = {
        company_id: companyId,
        status: 'CURRENT',
        search: '',
      };
      try {
        const response = await getAllShipmentsRTL(payload);
        const data = response?.data?.data;
        if (!Array.isArray(data)) {
          if (ownsScope()) setRtlSerials([]);
          return;
        }
        const serials = data
          .filter(
            (s: ShipmentResponse) => Array.isArray(s?.rtls) && s.rtls.length > 0
          )
          .flatMap((s: ShipmentResponse) => s.rtls.map((r) => r.serial))
          .filter((x): x is string => !!x);
        if (ownsScope()) setRtlSerials(serials);
      } catch {
        if (ownsScope()) setRtlSerials([]);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [companyId, generation, isCurrentScope]);

  // iMSecure (ioti) serials — their SSE channel id is the serial_number.
  useEffect(() => {
    if (!companyId || !isCurrentScope()) return;
    let cancelled = false;
    const ownsScope = () => !cancelled && isCurrentScope();
    const run = async () => {
      const serials = new Set<string>();
      try {
        let page = 1;
        let hasMore = true;
        while (hasMore && page <= MAX_DEVICE_LIST_PAGES) {
          const res = await listDevices({
            company_id: companyId,
            device_type: ['imsecure'],
            page,
            page_size: DEVICE_LIST_PAGE_SIZE,
          });
          if (!ownsScope()) return;
          const devices = res?.data?.data ?? [];
          devices.forEach((device) => {
            if (device.serial_number) serials.add(device.serial_number);
          });

          const pagination = res?.data?.pagination as
            | { total_pages?: number; has_more?: boolean }
            | undefined;
          if (
            typeof pagination?.total_pages === 'number' &&
            Number.isFinite(pagination.total_pages)
          ) {
            hasMore = page < Math.max(1, pagination.total_pages);
          } else if (typeof pagination?.has_more === 'boolean') {
            hasMore = pagination.has_more;
          } else {
            hasMore = false;
          }
          page += 1;
        }

        if (hasMore) {
          console.warn(
            `Stopped iMSecure notification discovery after ${MAX_DEVICE_LIST_PAGES} pages`
          );
        }
        if (ownsScope()) setImsecureSerials(Array.from(serials));
      } catch {
        // Keep successfully discovered pages subscribed if a later page fails.
        if (ownsScope()) setImsecureSerials(Array.from(serials));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [companyId, generation, isCurrentScope]);

  const markDeviceAlerting = useCallback(
    (deviceId: string) => {
      if (!companyId || !isCurrentScope()) return;
      setOverrides((prev) =>
        isCurrentScope() ? setOverride(prev, deviceId) : prev
      );
    },
    [companyId, isCurrentScope]
  );

  const handleSSEMessage = useCallback(
    (data: {
      timestamp: number;
      value: string;
      type: string;
      device_id: string;
    }) => {
      if (!companyId || !isCurrentScope()) return;
      setNotifications((prev) =>
        isCurrentScope() ? ingest(prev, data, Date.now(), genNotifId) : prev
      );
      if (isSealType(data.type)) markDeviceAlerting(data.device_id);
    },
    [companyId, isCurrentScope, markDeviceAlerting]
  );

  const sseDeviceIds = useMemo(
    () =>
      Array.from(new Set([...rtlSerials, ...imsecureSerials].filter(Boolean))),
    [rtlSerials, imsecureSerials]
  );
  const subscriptionChunks = useMemo(
    () =>
      sseDeviceIds.length > 0
        ? Array.from(
            { length: Math.ceil(sseDeviceIds.length / MAX_SOURCE_DEVICE_IDS) },
            (_, index) =>
              sseDeviceIds.slice(
                index * MAX_SOURCE_DEVICE_IDS,
                (index + 1) * MAX_SOURCE_DEVICE_IDS
              )
          )
        : [[]],
    [sseDeviceIds]
  );
  const handleSSEError = useCallback(
    (error: Event) => {
      if (isCurrentScope()) console.error('SSE Connection error:', error);
    },
    [isCurrentScope]
  );
  const markAsRead = useCallback(
    (notifId: string) => {
      if (!companyId || !isCurrentScope()) return;
      setNotifications((prev) =>
        isCurrentScope() ? markReadFn(prev, notifId) : prev
      );
    },
    [companyId, isCurrentScope]
  );
  const markAllAsRead = useCallback(() => {
    if (!companyId || !isCurrentScope()) return;
    setNotifications((prev) => (isCurrentScope() ? markAllReadFn(prev) : prev));
  }, [companyId, isCurrentScope]);
  const isDeviceAlerting = useCallback(
    (deviceId: string | null | undefined) => isAlerting(overrides, deviceId),
    [overrides]
  );

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount: unreadCountFn(notifications),
      markAsRead,
      markAllAsRead,
      isDeviceAlerting,
      markDeviceAlerting,
    }),
    [
      notifications,
      markAsRead,
      markAllAsRead,
      isDeviceAlerting,
      markDeviceAlerting,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {subscriptionChunks.map((deviceIds, index) => (
        <NotificationSubscription
          key={deviceIds[0] ?? `empty-${index}`}
          deviceIds={deviceIds}
          authToken={authToken}
          onMessage={handleSSEMessage}
          onError={handleSSEError}
        />
      ))}
      {children}
    </NotificationsContext.Provider>
  );
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [cookies] = useCookies(['user', 'token']);
  const companyId = resolveSessionCompanyId(
    cookies.user?.company_id,
    cookies.token
  );
  const authToken =
    companyId && typeof cookies.token === 'string' ? cookies.token : undefined;
  const activeScopeRef = useRef<NotificationScope>({
    companyId,
    generation: 0,
  });
  const generation =
    activeScopeRef.current.companyId === companyId
      ? activeScopeRef.current.generation
      : activeScopeRef.current.generation + 1;

  useLayoutEffect(() => {
    activeScopeRef.current = { companyId, generation };
  }, [companyId, generation]);

  return (
    <ScopedNotificationsProvider
      key={companyId ? `company:${companyId}` : 'company:none'}
      companyId={companyId}
      authToken={authToken}
      generation={generation}
      activeScopeRef={activeScopeRef}
    >
      {children}
    </ScopedNotificationsProvider>
  );
};
